import type { GoMotoFunction } from '../API/GoMoto.types';

/**
 * Regex for a valid email address. Enforces the mail-process ruleset: exactly one `@`,
 * no spaces, no parentheses, no commas or semicolons (which .NET treats as address
 * separators), no consecutive/leading/trailing dots, and a mandatory `provider.extension`.
 * An "atom" (`[^\s(),;@.]+`) allows any character except a space, parenthesis, comma,
 * semicolon, `@`, or dot — which covers letters, numbers, hyphen, underscore, plus, and
 * international/accented characters. No Unicode flag, so it is safe for the ES2017 target.
 */
export const VALID_EMAIL_REGEX: RegExp = /^[^\s(),;@.]+(?:\.[^\s(),;@.]+)*@[^\s(),;@.]+(?:\.[^\s(),;@.]+)+$/;

/**
 * Input-time block for email fields. Matches a value that contains none of the characters
 * the mail process cannot handle (spaces, parentheses, commas, semicolons). Used with
 * `onChangeRegexTest`, which keeps a keystroke only when the current value matches — so
 * this prevents typing/pasting those characters while still allowing a partially-typed
 * address.
 */
export const NO_INVALID_EMAIL_CHARS_REGEX: RegExp = /^[^\s(),;]+$/;

/**
 * The codes in `value` that are missing from the dealer's valid op code list, trimmed. A single-code field
 * is compared whole, so a comma inside it is reported as one invalid code; `allowMultiple` splits a
 * comma-separated field instead. `*` is always accepted, and an empty list means we have no op code data
 * for the dealer, so nothing is flagged.
 */
export const getInvalidOpCodes: GoMotoFunction<[string | null | undefined, string[], boolean?], string[]> = (value, opCodes, allowMultiple = false) => {
    if (opCodes.length === 0) {
        return [];
    }

    const codes: string[] = allowMultiple ? (value ?? '').split(',') : [value ?? ''];

    return codes
        .map((opCode: string) => opCode.trim())
        .filter((opCode: string) => !!opCode && opCode !== '*' && !opCodes.includes(opCode));
};

/**
 * True when {@link getInvalidOpCodes} finds any invalid code in `value`.
 */
export const hasInvalidOpCode: GoMotoFunction<[string | null | undefined, string[], boolean?], boolean> = (value, opCodes, allowMultiple = false) => {
    return getInvalidOpCodes(value, opCodes, allowMultiple).length > 0;
};
